import { useEffect, useState } from 'react'
import { api, type ZusammenBild, type ZusammenDetail, type ZusammenWahl } from '../api'
import { euro, menge, prozent, zahlwert } from '../format'
import { Karte } from '../komponenten/Karte'
import { Kennzahl } from '../komponenten/Kennzahl'

/* Artikel, deren Preis erst aus den Abfragen entsteht.
 *
 * "Arg. Filet" kostet nichts. Gebucht wird der Kopfartikel, dann fragt
 * die Kasse nach Groesse, Beilage und Dip - und aus den Antworten wird
 * der Preis. Diese Ansicht zeigt, was tatsaechlich gewaehlt wurde.
 *
 * Die Zuordnung ist hergeleitet, nicht abgelesen: das Archiv verknuepft
 * Kopf und Antwort nicht. Deshalb steht der Hinweis in der Ansicht und
 * nicht im Handbuch - wer die Zahl liest, soll wissen, woher sie kommt.
 */

const TITEL: Record<string, string> = {
  VARIANTE: 'Größe',
  BEISTELLUNG: 'Beilagen und Dips',
  ZUSATZ: 'Zusätze',
  ABWAHL: 'abgewählt',
  FOLGE: 'Reihenfolge',
}

const ERKLAERT: Record<string, string> = {
  VARIANTE: 'hier entsteht der Preis',
  BEISTELLUNG: 'Beilagen, Dips und Zutaten',
  ZUSATZ: '„+ …“, ohne eigenen Preis',
  ABWAHL: '„ohne …“, ohne eigenen Preis',
  FOLGE: '„NACH …“ — Anweisung an die Küche',
}

const REIHENFOLGE = ['VARIANTE', 'BEISTELLUNG', 'ZUSATZ', 'ABWAHL', 'FOLGE']

function Wahlen({ zeilen, gesamt }: { zeilen: ZusammenWahl[]; gesamt: number }) {
  const groesste = Math.max(...zeilen.map((z) => Math.abs(z.anzahl)), 1)
  return (
    <table>
      <thead>
        <tr>
          <th>Artikel</th>
          <th className="r">gewählt</th>
          <th className="r">Anteil</th>
          <th className="r">Umsatz</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {zeilen.map((z) => (
          <tr key={z.nummer}>
            <td>
              {z.artikel}
              {z.abfrage && (
                <span className="dezent" style={{ fontSize: 11, marginLeft: 7 }}>
                  {z.abfrage}
                </span>
              )}
            </td>
            <td className="r zahl">{menge(z.anzahl)}</td>
            <td className="r zahl dezent">{gesamt ? prozent((z.anzahl / gesamt) * 100) : '–'}</td>
            <td className="r zahl">
              {zahlwert(z.umsatz) === 0 ? <span className="dezent">–</span> : euro(z.umsatz)}
            </td>
            <td>
              <div className="zeilenbalken-spur">
                <div className="zeilenbalken"
                     style={{ width: `${Math.max(2, (Math.abs(z.anzahl) / groesste) * 100)}%` }} />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Aufschluesselung({ von, bis, betriebe, artikel, name }: {
  von: string; bis: string; betriebe: string[]; artikel: string; name: string
}) {
  const [d, setD] = useState<ZusammenDetail | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    let weg = false
    setD(null); setFehler(null)
    api.zusammenstellung(artikel, von, bis, betriebe)
      .then((x) => { if (!weg) setD(x) })
      .catch((e) => { if (!weg) setFehler(e.message) })
    return () => { weg = true }
  }, [artikel, von, bis, betriebe.join(',')])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (!d) return <div className="laedt">Wird geladen …</div>

  const stueck = d.kopf.anzahl
  const rollen = REIHENFOLGE.filter((r) => d.abfragen[r]?.length)

  return (
    <Karte titel={name}
           neben={`${menge(stueck)} × verkauft · ${euro(d.kopf.umsatz)} · Ø ${
             stueck ? euro(zahlwert(d.kopf.umsatz) / stueck) : '–'}`}>
      {rollen.length ? (
        <div className="gitter g-2">
          {rollen.map((r) => (
            <div key={r}>
              <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>
                {TITEL[r] ?? r}{' '}
                <span className="dezent" style={{ fontWeight: 400 }}>
                  — {ERKLAERT[r] ?? ''}
                </span>
              </h3>
              <Wahlen zeilen={d.abfragen[r]} gesamt={stueck} />
            </div>
          ))}
        </div>
      ) : (
        <div className="leer">Zu diesem Artikel sind keine Abfragen gebucht.</div>
      )}
    </Karte>
  )
}

export function Zusammenstellungen({ von, bis, betriebe }: {
  von: string; bis: string; betriebe: string[]
}) {
  const [d, setD] = useState<ZusammenBild | null>(null)
  const [offen, setOffen] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  useEffect(() => {
    let weg = false
    setLaedt(true); setFehler(null)
    api.zusammenstellungen(von, bis, betriebe)
      .then((x) => { if (!weg) { setD(x); setOffen(x.artikel[0]?.nummer ?? null) } })
      .catch((e) => { if (!weg) setFehler(e.message) })
      .finally(() => { if (!weg) setLaedt(false) })
    return () => { weg = true }
  }, [von, bis, betriebe.join(',')])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (laedt && !d) return <div className="laedt">Wird geladen …</div>
  if (!d) return null

  if (!d.artikel.length) {
    return (
      <Karte titel="Zusammenstellungen">
        <div className="leer">
          Im Zeitraum ist kein Artikel gebucht, dessen Preis erst aus einer
          Abfrage entsteht.
        </div>
      </Karte>
    )
  }

  // Zähler und Nenner kommen getrennt aus der API; geteilt wird erst hier.
  const teil = zahlwert(d.anteil.umsatz_zusammenstellungen)
  const ganz = zahlwert(d.anteil.umsatz_gesamt)
  const name = d.artikel.find((a) => a.nummer === offen)?.artikel ?? ''

  return (
    <>
      <div className="gitter g-3">
        <Kennzahl etikett="verkauft" wert={menge(d.anteil.anzahl)}
                  fuss="zusammengestellte Artikel, netto nach Storno" />
        <Kennzahl etikett="Umsatz daraus" wert={euro(teil)}
                  fuss="Variante, Beilagen und Dips zusammen" />
        <Kennzahl etikett="Anteil am Umsatz"
                  wert={ganz ? prozent((teil / ganz) * 100) : '–'}
                  fuss={`von ${euro(ganz)} gebuchtem Umsatz, ohne Trinkgeld`} />
      </div>

      <Karte titel="Zusammenstellungen"
             neben="Artikel ohne eigenen Preis — er entsteht aus den Abfragen">
        <table>
          <thead>
            <tr>
              <th>Artikel</th>
              <th className="r">verkauft</th>
              <th className="r">Umsatz</th>
              <th className="r">Ø je Stück</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {d.artikel.map((a) => (
              <tr key={a.nummer}>
                <td>{a.artikel}</td>
                <td className="r zahl">{menge(a.anzahl)}</td>
                <td className="r zahl">{euro(a.umsatz)}</td>
                <td className="r zahl">
                  {a.anzahl ? euro(zahlwert(a.umsatz) / a.anzahl) : '–'}
                </td>
                <td className="r">
                  <button className="verweis" aria-pressed={a.nummer === offen}
                          onClick={() => setOffen(a.nummer)}>
                    {a.nummer === offen ? 'Abfragen ▾' : 'Abfragen'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="merker" style={{ marginTop: 14 }}>{d.hinweis}</p>
      </Karte>

      {offen && (
        <div style={{ marginTop: 14 }}>
          <Aufschluesselung von={von} bis={bis} betriebe={betriebe}
                            artikel={offen} name={name} />
        </div>
      )}
    </>
  )
}
